import { auth } from './firebase';
import * as Sentry from '@sentry/react';
import { trackEvent } from './analytics';

export const API_BASE_URL = import.meta.env.VITE_API_URL || '';

async function getAuthToken(): Promise<string | null> {
  try {
    await auth.authStateReady();
  } catch (err) {
    console.error('Error waiting for auth state ready:', err);
  }

  // Poll briefly if currentUser is not yet set, in case we are in a signup/login transition
  for (let i = 0; i < 10; i++) {
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        return await currentUser.getIdToken();
      } catch {
        return null;
      }
    }
    await new Promise((resolve) => setTimeout(resolve, 50));
  }

  return null;
}

export class ApiError extends Error {
  status?: number;
  constructor(message: string, status?: number) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

let consecutiveFailures = 0;

export async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = await getAuthToken();
  const method = (options.method || 'GET').toUpperCase();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let httpStatus: number | undefined;

  try {
    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers
    });

    httpStatus = response.status;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(errorData.error || `API Request failed (${response.status})`, response.status);
    }

    consecutiveFailures = 0;
    return response.json();
  } catch (error) {
    consecutiveFailures++;

    Sentry.captureException(error, {
      tags: {
        api_url: url,
        api_method: method,
        http_status: httpStatus ?? 'network_error',
        consecutive_failures: consecutiveFailures,
      },
      extra: { url, method, httpStatus, consecutiveFailures },
    });

    if (consecutiveFailures === 3) {
      Sentry.captureMessage('Backend appears unavailable: 3 consecutive API failures mid-session', {
        level: 'error',
        tags: { scenario: 'backend_unreachable_mid_session' },
        extra: { lastUrl: url, lastMethod: method },
      });
      trackEvent('backend_unreachable_detected', { consecutive_failures: 3, last_url: url });
    }

    throw error;
  }
}
